import React, { useContext } from 'react';
import { bool, func, number, shape, string } from 'prop-types';
import { Box, Button, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';

// Icons
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

// Context
import { DrawerContext } from 'common/drawer/DrawerContext';

// Utils
import { DRAWER_TYPES } from 'utils/constants';
import { setDialogDetails } from 'feature/loading-management/supplier-authorizations/store/supplierAuthorizationSlice';
import { useUserRolePermissions } from 'hooks';
import { useTheme } from '@mui/material/styles';
import allCarrierAuthSteps from '../../create-new-steps/Steps';

// Components
import CreateWizard from '../../create-new-steps/CreateWizard';

// Styles
import {
  filterWrapStyles,
  headerClassStyles,
  headerContainerStyles,
  subHeaderClassStyles,
} from './CardsPanelHeader.styles';

// Non-Primitives
// Defined outside of component to keep them from garbage collection on rerenders

// MuiThemeObject -> CSSStyleDeclaration
const makeAddNewButtonStyles = theme => ({ marginLeft: theme.spacing(3) }); // 'New Authorizations' Button's `sx` prop value
const filtersButtonStyles = { alignSelf: 'end' }; // 'Filters' Button's `sx` prop value

const CardsPanelHeader = ({
  carrierAuthorizationsCardsLength,
  filtersOpen,
  filtersRef,
  filterIdentifierCount,
  pending,
  resetCarrierAuthWizardData,
  setFiltersOpen,
  subtitle,
  title,
}) => {
  const theme = useTheme();
  const { viewBy } = useParams();
  const { setDrawerComponentProps } = useContext(DrawerContext);
  const { isProductLoadingManage } = useUserRolePermissions();

  const handleNewAuthorizations = () => {
    setDrawerComponentProps({
      Component: props => (
        <CreateWizard steps={allCarrierAuthSteps} title="New Carrier Authorizations" {...props} />
      ),
      drawerPaperSx: { overflowY: 'hidden', width: { lg: 760, xs: 1 } },
      forceCloseSelector: state => state.loadingManagement.createNewCarrierAuth.forceClose,
      holdOpenSelector: state => state.loadingManagement.createNewCarrierAuth.submittingBatch,
      resetWizard: () => resetCarrierAuthWizardData(viewBy),
      setCurrentDialogOpen: () =>
        setDialogDetails({
          header: 'Are you sure you want to close the wizard?',
          message:
            'By closing the wizard you will no longer be able to view the progress of each activation however, all requests will continue until complete. Please confirm you want to close the wizard.',
        }),
      type: DRAWER_TYPES.createNewCarrierAuth,
    });
  };

  return (
    <Box sx={headerContainerStyles(theme)}>
      <Box>
        <Typography noWrap sx={headerClassStyles} variant="h5">
          {title}
        </Typography>
        {subtitle && (
          <Typography noWrap sx={subHeaderClassStyles} variant="body2">
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box justifySelf="end">
        <Button
          disabled={pending || carrierAuthorizationsCardsLength <= 1}
          onClick={() => setFiltersOpen(!filtersOpen)}
          onClose={() => setFiltersOpen(false)}
          ref={filtersRef}
          sx={filtersButtonStyles}
          variant="text"
        >
          Filters
          {filterIdentifierCount >= 1 && (
            <Typography sx={filterWrapStyles}>{filterIdentifierCount}</Typography>
          )}
          {filtersOpen ? (
            <ExpandLessRoundedIcon position="right" variant="buttonsChild" />
          ) : (
            <ExpandMoreRoundedIcon position="right" variant="buttonsChild" />
          )}
        </Button>
        {isProductLoadingManage && (
          <Button
            onClick={() => handleNewAuthorizations()}
            sx={makeAddNewButtonStyles}
            variant="contained"
          >
            New Authorizations
          </Button>
        )}
      </Box>
    </Box>
  );
};

CardsPanelHeader.propTypes = {
  carrierAuthorizationsCardsLength: number.isRequired,
  filterIdentifierCount: number.isRequired,
  filtersOpen: bool.isRequired,
  filtersRef: shape({}).isRequired,
  pending: bool.isRequired,
  resetCarrierAuthWizardData: func.isRequired,
  setFiltersOpen: func.isRequired,
  subtitle: string,
  title: string.isRequired,
};

CardsPanelHeader.defaultProps = {
  subtitle: undefined,
};

export default CardsPanelHeader;
